import React, {useEffect, useMemo, useRef, useState} from "react";
import {SaveIcon, UploadIcon, UserIcon} from "lucide-react";
import Button from "../../../../../components/Button";
import {MarkdownField} from "../../../../../components/MarkdownField";
import {notify} from "../../../../../components/Notifications";
import {MAX_UPLOAD_BYTES, MAX_UPLOAD_MB} from "../../../../../lib/uploads";
import {joinRoles, SPEAKER_ROLES} from "../../../../../lib/eventProgramme";
import type {EventSpeaker, SpeakerRole} from "../../../../../types/event";

export type SpeakerDraft = {
    name: string;
    position: string;
    organisation: string;
    tagline: string;
    bio: string;
    roles: SpeakerRole[];
};

/** The "dto" part of a speaker POST or PUT, with blanks sent as null. */
export function speakerDto(d: SpeakerDraft, removeImage: boolean) {
    const blank = (s: string) => s.trim() || null;
    return {
        name: d.name.trim(),
        position: blank(d.position),
        organisation: blank(d.organisation),
        tagline: blank(d.tagline),
        bio: blank(d.bio),
        roles: d.roles,
        removeImage,
    };
}

const inputCls =
    "mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none";

/**
 * Add or edit one speaker. Roles keep the order they were ticked in: the first is the speaker's
 * default role in line-up slots and leads their card.
 */
export default function SpeakerEditor({
                                          speaker,
                                          saving,
                                          onSave,
                                          onCancel,
                                      }: {
    speaker: EventSpeaker | null;
    saving: boolean;
    onSave: (draft: SpeakerDraft, file: File | null, removeImage: boolean) => void;
    onCancel: () => void;
}) {
    const [draft, setDraft] = useState<SpeakerDraft>(() => ({
        name: speaker?.name ?? "",
        position: speaker?.position ?? "",
        organisation: speaker?.organisation ?? "",
        tagline: speaker?.tagline ?? "",
        bio: speaker?.bio ?? "",
        roles: speaker?.roles?.length ? [...speaker.roles] : ["SPEAKER"],
    }));
    const [file, setFile] = useState<File | null>(null);
    const [removeImage, setRemoveImage] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const filePreview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => () => {
        if (filePreview) URL.revokeObjectURL(filePreview);
    }, [filePreview]);

    const imageUrl = filePreview ?? (removeImage ? null : speaker?.imageUrl ?? null);
    const set = (patch: Partial<SpeakerDraft>) => setDraft((d) => ({...d, ...patch}));

    const toggleRole = (role: SpeakerRole) =>
        set({roles: draft.roles.includes(role) ? draft.roles.filter((r) => r !== role) : [...draft.roles, role]});

    const canSave = draft.name.trim().length > 0 && draft.roles.length > 0 && !saving;

    const pick = (f: File | null) => {
        if (!f) return;
        if (f.size > MAX_UPLOAD_BYTES) {
            notify.error(`Image too large (max ${MAX_UPLOAD_MB}MB)`);
            return;
        }
        setFile(f);
        setRemoveImage(false);
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {speaker ? "Edit speaker" : "New speaker"}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Speakers appear on the event page and can be added to line-up slots.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="secondary" size="md" onClick={onCancel} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={() => onSave(draft, file, removeImage && !file)}
                        disabled={!canSave}
                        leftIcon={saving ? undefined : <SaveIcon className="size-4" aria-hidden="true"/>}
                    >
                        {saving ? "Saving…" : "Save speaker"}
                    </Button>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="space-y-4 lg:col-span-2">
                    <div>
                        <label htmlFor="speaker-name" className="block text-sm font-medium text-gray-900">Name *</label>
                        <input id="speaker-name" value={draft.name} maxLength={120} required
                               onChange={(e) => set({name: e.target.value})} className={inputCls}
                               placeholder="e.g. Ndapewa Shikongo"/>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label htmlFor="speaker-position" className="block text-sm font-medium text-gray-900">
                                Position
                            </label>
                            <input id="speaker-position" value={draft.position} maxLength={120}
                                   onChange={(e) => set({position: e.target.value})} className={inputCls}
                                   placeholder="e.g. Chief Executive"/>
                        </div>
                        <div>
                            <label htmlFor="speaker-organisation" className="block text-sm font-medium text-gray-900">
                                Organisation
                            </label>
                            <input id="speaker-organisation" value={draft.organisation} maxLength={120}
                                   onChange={(e) => set({organisation: e.target.value})} className={inputCls}
                                   placeholder="e.g. Acme Namibia"/>
                        </div>
                    </div>

                    <fieldset>
                        <legend className="block text-sm font-medium text-gray-900">Roles *</legend>
                        <p className="mt-1 text-xs text-gray-500">
                            Tick one or more. The first one you tick is their default role in line-up slots.
                        </p>
                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                            {SPEAKER_ROLES.map((r) => (
                                <label key={r.value} className="flex items-center gap-2 text-sm text-gray-800">
                                    <input
                                        type="checkbox"
                                        checked={draft.roles.includes(r.value)}
                                        onChange={() => toggleRole(r.value)}
                                        className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                    />
                                    {r.label}
                                </label>
                            ))}
                        </div>
                        <p className="mt-2 text-xs text-gray-600" aria-live="polite">
                            {draft.roles.length
                                ? <>Shown as: <span className="font-medium">{joinRoles(draft.roles)}</span></>
                                : <span className="text-red-700">Pick at least one role.</span>}
                        </p>
                    </fieldset>

                    <div>
                        <label htmlFor="speaker-tagline" className="block text-sm font-medium text-gray-900">Tagline</label>
                        <input id="speaker-tagline" value={draft.tagline} maxLength={160}
                               onChange={(e) => set({tagline: e.target.value})} className={inputCls}
                               placeholder="One line shown on their card"/>
                    </div>

                    <MarkdownField
                        title="Bio"
                        description="Shown when a visitor opens the speaker. Markdown is supported."
                        value={draft.bio}
                        onChange={(v) => set({bio: v.slice(0, 10000)})}
                    />
                </div>

                <div className="space-y-3">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm font-semibold text-gray-900">Preview</div>
                        <div className="mt-3 flex flex-col items-center text-center">
                            <div
                                className="size-32 overflow-hidden rounded-full bg-white ring-1 ring-black/5">
                                {imageUrl ? (
                                    <img src={imageUrl} alt="" className="size-full object-cover"/>
                                ) : (
                                    <div className="flex size-full items-center justify-center text-gray-400">
                                        <UserIcon className="size-10" aria-hidden="true"/>
                                    </div>
                                )}
                            </div>
                            <p className="mt-3 text-xs text-gray-500">
                                Photos are cropped to a circle on the event page. Keep the face centred.
                            </p>
                        </div>
                    </div>

                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => {
                            pick(e.target.files?.[0] ?? null);
                            e.currentTarget.value = "";
                        }}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            disabled={saving}
                            onClick={() => fileRef.current?.click()}
                            leftIcon={<UploadIcon className="size-4" aria-hidden="true"/>}
                        >
                            {imageUrl ? "Change photo" : "Upload photo"}
                        </Button>
                        {file ? (
                            <button type="button" className="text-sm font-medium text-gray-600 hover:text-gray-900"
                                    disabled={saving} onClick={() => setFile(null)}>
                                Clear selected file
                            </button>
                        ) : speaker?.imageUrl && !removeImage ? (
                            <button type="button" className="text-sm font-medium text-gray-600 hover:text-gray-900"
                                    disabled={saving} onClick={() => setRemoveImage(true)}>
                                Remove photo
                            </button>
                        ) : null}
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPEG or WebP, up to {MAX_UPLOAD_MB}MB.</p>
                </div>
            </div>
        </div>
    );
}
